<template>
  <div class="rte" :class="{ 'rte--invalid': invalid }">
    <div v-if="editor" class="rte__toolbar">
      <button type="button" class="rte__tb-btn" title="Undo" @click="chain().undo().run()">
        <i class="pi pi-replay" aria-hidden="true"></i>
      </button>
      <button type="button" class="rte__tb-btn" title="Redo" @click="chain().redo().run()">
        <i class="pi pi-refresh" aria-hidden="true"></i>
      </button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <select class="rte__tb-select rte__tb-select--zoom" aria-label="Zoom" v-model.number="zoom">
        <option v-for="z in zoomOptions" :key="z" :value="z">{{ z }}%</option>
      </select>

      <select
        v-if="fullToolbar"
        class="rte__tb-select"
        aria-label="Block style"
        :value="currentBlock"
        @change="setBlock($event.target.value)"
      >
        <option v-for="block in blockOptions" :key="block.value" :value="block.value">
          {{ block.label }}
        </option>
      </select>

      <select
        class="rte__tb-select"
        aria-label="Font family"
        :value="currentFontFamily"
        @change="setFontFamily($event.target.value)"
      >
        <option value="">Font</option>
        <option v-for="font in fontFamilies" :key="font.value" :value="font.value">
          {{ font.label }}
        </option>
      </select>

      <select
        class="rte__tb-select rte__tb-select--small"
        aria-label="Font size"
        :value="currentFontSize"
        @change="setFontSize($event.target.value)"
      >
        <option value="">Size</option>
        <option v-for="size in fontSizeOptions" :key="size" :value="String(size)">
          {{ size }}
        </option>
      </select>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--bold"
        :class="{ 'is-active': editor.isActive('bold') }"
        title="Bold"
        @click="chain().toggleBold().run()"
      >B</button>
      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--italic"
        :class="{ 'is-active': editor.isActive('italic') }"
        title="Italic"
        @click="chain().toggleItalic().run()"
      >I</button>
      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--underline"
        :class="{ 'is-active': editor.isActive('underline') }"
        title="Underline"
        @click="chain().toggleUnderline().run()"
      >U</button>
      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--strike"
        :class="{ 'is-active': editor.isActive('strike') }"
        title="Strikethrough"
        @click="chain().toggleStrike().run()"
      >S</button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <label class="rte__tb-btn" title="Highlight">
        <span class="rte__tb-swatch" :style="{ backgroundColor: currentHighlight }"></span>
        <input
          type="color"
          class="rte__tb-color-input"
          :value="currentHighlight"
          @input="setHighlight($event.target.value)"
        />
      </label>
      <label class="rte__tb-btn rte__tb-btn--text-color" title="Text color">
        <span>A</span>
        <span class="rte__tb-color-bar" :style="{ backgroundColor: currentColor }"></span>
        <input
          type="color"
          class="rte__tb-color-input"
          :value="currentColor"
          @input="setColor($event.target.value)"
        />
      </label>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': editor.isActive('link') }"
        title="Link"
        @click="toggleLink()"
      >
        <i class="pi pi-link" aria-hidden="true"></i>
      </button>
      <button type="button" class="rte__tb-btn" title="Image" @click="addImage()">
        <i class="pi pi-image" aria-hidden="true"></i>
      </button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        title="Bullet list"
        @click="chain().toggleBulletList().run()"
      >
        <i class="pi pi-list" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        title="Numbered list"
        @click="chain().toggleOrderedList().run()"
      >
        <i class="pi pi-sort-numeric-down" aria-hidden="true"></i>
      </button>

      <template v-if="fullToolbar">
        <button
          type="button"
          class="rte__tb-btn"
          title="Decrease indent"
          @click="indent(-1)"
        >
          <i class="pi pi-angle-double-left" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="rte__tb-btn"
          title="Increase indent"
          @click="indent(1)"
        >
          <i class="pi pi-angle-double-right" aria-hidden="true"></i>
        </button>
      </template>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': isAlign('left') }"
        title="Align left"
        @click="setAlign('left')"
      >
        <i class="pi pi-align-left" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': isAlign('center') }"
        title="Align center"
        @click="setAlign('center')"
      >
        <i class="pi pi-align-center" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': isAlign('right') }"
        title="Align right"
        @click="setAlign('right')"
      >
        <i class="pi pi-align-right" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': isAlign('justify') }"
        title="Justify"
        @click="setAlign('justify')"
      >
        <i class="pi pi-align-justify" aria-hidden="true"></i>
      </button>

      <select
        class="rte__tb-select rte__tb-select--small"
        aria-label="Line spacing"
        :value="currentLineHeight"
        @change="setLineHeight($event.target.value)"
      >
        <option value="">Line</option>
        <option v-for="lineHeight in lineHeightOptions" :key="lineHeight" :value="String(lineHeight)">
          {{ lineHeight }}
        </option>
      </select>

      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': isRtlActive }"
        title="Right to left"
        @click="toggleRtl()"
      >RTL</button>

      <button
        v-if="fullToolbar"
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': isSplitActive }"
        title="Split row - push the pieces to opposite ends"
        @click="toggleSplit()"
      >
        <i class="pi pi-arrows-h" aria-hidden="true"></i>
      </button>

      <button type="button" class="rte__tb-btn" title="Clear formatting" @click="clearFormat()">
        <i class="pi pi-eraser" aria-hidden="true"></i>
      </button>

      <div v-if="fullToolbar" class="rte-chars">
        <button
          ref="characterButton"
          type="button"
          class="rte__tb-btn"
          title="Special character"
          @click="toggleCharacterMenu()"
        >
          <i class="pi pi-at" aria-hidden="true"></i>
        </button>
        <Teleport to="body">
          <div
            v-if="showCharacterMenu"
            ref="characterMenu"
            class="rte-chars__menu"
            :style="characterMenuStyle"
          >
            <button
              v-for="character in specialCharacters"
              :key="character"
              type="button"
              :title="character"
              @click="insertCharacter(character)"
            >
              {{ character }}
            </button>
          </div>
        </Teleport>
      </div>

      <!-- Only while a photo slot is selected. The box can also be dragged by
           its own handles; this is the same size in figures, for when the box is
           too small to grab comfortably or the author wants a round number.

           Each button suppresses the default mousedown so pressing it never
           takes focus off the canvas. What is selected here is a whole node
           rather than a run of text, and a node selection is the fragile kind:
           letting the editor blur invites the browser to hand ProseMirror a
           different selection on the way back, and the press would then land on
           nothing. -->
      <template v-if="isEventImageActive">
        <span class="rte__tb-divider" aria-hidden="true"></span>

        <div class="rte-photo">
          <span class="rte-photo__label">Photo</span>
          <button
            type="button"
            class="rte__tb-btn"
            title="Smaller"
            @mousedown.prevent
            @click="stepEventImageWidth(-1)"
          >
            <i class="pi pi-minus" aria-hidden="true"></i>
          </button>
          <span class="rte-photo__value" title="Width × height, as a share of the artboard's width">
            {{ eventImageSizeLabel }}
          </span>
          <button
            type="button"
            class="rte__tb-btn"
            title="Larger"
            @mousedown.prevent
            @click="stepEventImageWidth(1)"
          >
            <i class="pi pi-plus" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="rte__tb-btn"
            title="Reset the box back to a 3:2 photo shape"
            @mousedown.prevent
            @click="resetEventImageShape()"
          >
            <i class="pi pi-replay" aria-hidden="true"></i>
          </button>
        </div>
      </template>

      <template v-if="variables.length || variableAction">
        <div class="rte__tb-spacer"></div>

        <div class="rte-variable">
          <button
            ref="variableButton"
            type="button"
            class="rte-variable__btn"
            @click="toggleVariableMenu()"
          >
            <span>Variable</span>
            <i class="pi pi-chevron-down" aria-hidden="true"></i>
          </button>
          <Teleport to="body">
            <div
              v-if="showVariableMenu"
              ref="variableMenu"
              class="rte-variable__menu"
              :class="{ 'rte-variable__menu--rich': variableMenuIsRich }"
              :style="variableMenuStyle"
            >
              <button
                v-for="variable in variables"
                :key="variable.value"
                type="button"
                class="rte-variable__item"
                @click="insertVariable(variable.value)"
              >
                <span class="rte-variable__label">{{ variable.label }}</span>
                <span v-if="variable.required" class="rte-variable__required">Required</span>
              </button>
              <template v-if="variableAction">
                <div v-if="variables.length" class="rte-variable__divider" aria-hidden="true"></div>
                <button type="button" class="rte-variable__action" @click="runVariableAction()">
                  <i class="pi" :class="variableAction.icon || 'pi-plus'" aria-hidden="true"></i>
                  <span>{{ variableAction.label }}</span>
                </button>
              </template>
            </div>
          </Teleport>
        </div>
      </template>
    </div>

    <div class="rte__canvas" :style="{ zoom: String(zoom / 100) }">
      <EditorContent class="rte__editor" :editor="editor" :style="editorStyle" />
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { Editor, EditorContent, VueNodeViewRenderer } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import FontFamily from '@tiptap/extension-font-family';
import { Extension, Node as TiptapNode } from '@tiptap/core';
import EventImagePlaceholder from '@/components/Event_Image_Placeholder.vue';
import {
  MINYANIM_ROLL_LINE_HEIGHT_OPTIONS,
  MINYANIM_TEXT_EDITOR_FONT_OPTIONS,
  MINYANIM_TEXT_EDITOR_ZOOM_OPTIONS,
  ZMANIM_N_FONT_SIZE_OPTIONS,
} from '@/utils/text_editor_options';
import {
  storedCustomFonts,
  withCustomFontStackOptions,
} from '@/utils/custom_fonts';
import {
  EVENT_IMAGE_DEFAULT_RATIO,
  EVENT_IMAGE_DEFAULT_WIDTH,
  EVENT_IMAGE_RATIO_ATTR,
  EVENT_IMAGE_SLOT_ATTR,
  EVENT_IMAGE_TOKEN,
  EVENT_IMAGE_WIDTH_ATTR,
  EVENT_IMAGE_WIDTH_STEP,
  clampEventImageRatio,
  clampEventImageWidth,
  eventImageHeightPercent,
  eventImageSlotAttributes,
} from '@/utils/event_template_image';

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => {
              const value = element.style.fontSize || '';
              if (!value) return null;
              return value.endsWith('px') ? value.replace('px', '') : value;
            },
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              const value = String(attributes.fontSize);
              const fontSize = /^\d+(\.\d+)?$/.test(value) ? `${value}px` : value;
              return {
                style: `font-size: ${fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const LineHeight = Extension.create({
  name: 'lineHeight',
  addOptions() {
    return {
      types: ['paragraph', 'heading'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {};
              return {
                style: `line-height: ${attributes.lineHeight}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setLineHeight:
        (lineHeight) =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { lineHeight })),
      unsetLineHeight:
        () =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { lineHeight: null })),
    };
  },
});

const TextDirection = Extension.create({
  name: 'textDirection',
  addOptions() {
    return {
      types: ['paragraph', 'heading'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textDirection: {
            default: null,
            parseHTML: (element) => element.getAttribute('dir') || null,
            renderHTML: (attributes) => {
              if (!attributes.textDirection) return {};
              return {
                dir: attributes.textDirection,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setTextDirection:
        (textDirection) =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { textDirection })),
      unsetTextDirection:
        () =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { textDirection: null })),
    };
  },
});

// Per-variable alignment inside a split row. Adds an `inlineAlign` attribute to
// the textStyle mark: the selection becomes its own flex column (flex: 1) and
// aligns its text within it, so each variable can be aligned independently.
// Inert outside a split row. Byte-for-byte the same output as the Minyanim Roll
// grid designer's, so a layout authored in either place round-trips.
const InlineAlign = Extension.create({
  name: 'inlineAlign',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          inlineAlign: {
            default: null,
            parseHTML: (element) => element.style.textAlign || null,
            renderHTML: (attributes) => {
              if (!attributes.inlineAlign) return {};
              return { style: `flex: 1 1 0%; text-align: ${attributes.inlineAlign}` };
            },
          },
        },
      },
    ];
  },
});

// "Split row" — lays the paragraph out as a flex row with space-between, so its
// pieces are pushed to opposite ends. Marked with data-split so it round-trips
// and can be detected on parse. Same extension as the Minyanim Roll grid
// designer's, including the inline style, so the two produce identical HTML.
const SPLIT_ROW_STYLE = 'display: flex; align-items: center; justify-content: space-between; gap: 12px';
const SplitRow = Extension.create({
  name: 'splitRow',
  addOptions() {
    return { types: ['paragraph', 'heading'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          splitRow: {
            default: false,
            parseHTML: (element) => element.getAttribute('data-split') === 'true',
            renderHTML: (attributes) => {
              if (!attributes.splitRow) return {};
              return { 'data-split': 'true', style: SPLIT_ROW_STYLE };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setSplitRow:
        () =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { splitRow: true })),
      unsetSplitRow:
        () =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { splitRow: false })),
    };
  },
});

// Block indentation, as `padding-inline-start` in ems so it follows the text
// direction — TinyMCE's indent / outdent, which the editors that used it before
// would otherwise lose. Inside a list the toolbar nests the item instead; this
// covers every other block.
const INDENT_STEP_EM = 2;
const INDENT_MAX_EM = 16;
const Indent = Extension.create({
  name: 'indent',
  addOptions() {
    return { types: ['paragraph', 'heading'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: null,
            parseHTML: (element) => {
              const value = parseFloat(element.style.paddingInlineStart || element.style.paddingLeft || '');
              return Number.isFinite(value) && value > 0 ? value : null;
            },
            renderHTML: (attributes) => {
              if (!attributes.indent) return {};
              return { style: `padding-inline-start: ${attributes.indent}em` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setIndent:
        (indent) =>
        ({ commands }) => {
          const next = Math.min(Math.max(Number(indent) || 0, 0), INDENT_MAX_EM);
          return this.options.types.some((type) =>
            commands.updateAttributes(type, { indent: next > 0 ? next : null })
          );
        },
    };
  },
});

// The Event Template's photo slot: the `{image}` token, wrapped in a box whose
// size the author sets.
//
// An atom, and inline, because that is what the token it replaces was — a single
// uneditable thing sitting in a run of text. It serialises with the token still
// inside it, so `renderEventContent` in Display.vue goes on substituting exactly
// the way it always has and the event's <img> simply lands inside the box. See
// utils/event_template_image.ts for the geometry and for why it is a percentage.
//
// Loaded by every editor whether or not its Variable menu offers a photo, for
// the same reason InlineAlign and SplitRow are: a template that holds a slot has
// to round-trip through any editor that opens it rather than be stripped on
// parse.
const EventImage = TiptapNode.create({
  name: 'eventImage',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  // Left off deliberately. The box is resized by dragging its own handles, and a
  // node-level drag would fight those for the same gesture.
  draggable: false,

  addAttributes() {
    return {
      // Both render as part of the slot's attributes below, where the style can
      // be written from the pair of them at once.
      width: {
        default: EVENT_IMAGE_DEFAULT_WIDTH,
        parseHTML: (element) => clampEventImageWidth(element.getAttribute(EVENT_IMAGE_WIDTH_ATTR)),
        renderHTML: () => ({}),
      },
      ratio: {
        default: EVENT_IMAGE_DEFAULT_RATIO,
        parseHTML: (element) => clampEventImageRatio(element.getAttribute(EVENT_IMAGE_RATIO_ATTR)),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    // Above the default priority of 50, because the slot is a styled <span> and
    // TextStyle claims every one of those (`tag: 'span'`, matched on it having a
    // style attribute at all). ProseMirror gathers mark rules before node rules
    // and takes the first that matches, so at equal priority the slot would be
    // read as a run of marked text and the box would come back as a bare
    // `{image}` — which is exactly what an Edit modal does that an Add modal,
    // inserting the node through a command rather than parsing HTML, does not.
    return [{ tag: `span[${EVENT_IMAGE_SLOT_ATTR}]`, priority: 100 }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      { ...HTMLAttributes, ...eventImageSlotAttributes(node.attrs.width, node.attrs.ratio) },
      EVENT_IMAGE_TOKEN,
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(EventImagePlaceholder);
  },
});

// The blocks TinyMCE's "Blocks" dropdown offered. Heading is part of StarterKit,
// so this is only the menu over it.
const BLOCK_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'h5', label: 'Heading 5' },
  { value: 'h6', label: 'Heading 6' },
];

// Stand-in for TinyMCE's charmap / emoticons: the characters those menus are
// actually reached for, without pulling in a picker library.
const SPECIAL_CHARACTERS = [
  '–', '—', '•', '·', '…', '«', '»', '“', '”', '‘', '’',
  '★', '☆', '✦', '✿', '❖', '♦', '♥', '✓', '✔', '✗',
  '°', '±', '×', '÷', '≈', '≠', '≤', '≥', '←', '→', '↑', '↓',
  '©', '®', '™', '§', '¶', '№', '₪', '$', '€', '£',
  '😀', '😊', '🎉', '🎊', '🕯️', '🕎', '✡️', '📅', '📍', '🙏',
];

// Empty Hebrew-friendly starting paragraph (RTL, centered) so an empty editor
// matches the authoring direction used across the app.
const DEFAULT_EDITOR_CONTENT = '<p dir="rtl" style="text-align: center"></p>';

export default {
  name: 'Rich_Text_Editor',
  components: {
    EditorContent,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    // Renders the editor with a danger border (e.g. failed validation).
    invalid: {
      type: Boolean,
      default: false,
    },
    // Height of the editable area.
    height: {
      type: String,
      default: '320px',
    },
    // The Variable menu: { value, label }, and `required: true` to tag an
    // entry Required.
    variables: {
      type: Array,
      default: () => [],
    },
    // An entry at the foot of the Variable menu — { label, icon } — that emits
    // `variable-action` (the donation templates' "New field").
    variableAction: {
      type: Object,
      default: null,
    },
    /**
     * Reveals the rest of the Minyanim Roll design toolbar plus the blocks,
     * indent and character controls TinyMCE offered: block style, indent /
     * outdent, split row (with per-variable alignment inside it) and special
     * characters.
     *
     * Opt-in rather than always on so the editors that already ship with the
     * short toolbar keep exactly the controls they have today — nothing about
     * the stored HTML changes either way, only which buttons are offered.
     */
    fullToolbar: {
      type: Boolean,
      default: false,
    },
    /**
     * Extra TipTap extensions, appended after this editor's own — for an editor
     * that needs nodes of its own (the Weekly Schedule design canvas brings its
     * Event card this way). Read once, when the editor is created.
     */
    extensions: {
      type: Array,
      default: () => [],
    },
    /**
     * Options handed to StarterKit, e.g. `{ document: false }` when one of the
     * `extensions` supplies the document node. Read once, at creation. Unset,
     * StarterKit is used exactly as before.
     */
    starterKitOptions: {
      type: Object,
      default: null,
    },
  },
  // `normalized` carries the document exactly as the editor writes it, each
  // time content is loaded rather than typed — on creation and after a new
  // `modelValue` arrives — so a parent can tell an edit from a re-serialization.
  emits: ['update:modelValue', 'normalized', 'variable-action'],
  data() {
    return {
      editor: null,
      fontSizeOptions: ZMANIM_N_FONT_SIZE_OPTIONS,
      lineHeightOptions: MINYANIM_ROLL_LINE_HEIGHT_OPTIONS,
      zoomOptions: MINYANIM_TEXT_EDITOR_ZOOM_OPTIONS,
      zoom: 100,
      blockOptions: BLOCK_OPTIONS,
      specialCharacters: SPECIAL_CHARACTERS,
      toolbarStateKey: 0,
      showCharacterMenu: false,
      showVariableMenu: false,
      variableMenuStyle: {
        top: '0px',
        left: '0px',
        visibility: 'hidden',
      },
      characterMenuStyle: {
        top: '0px',
        left: '0px',
        visibility: 'hidden',
      },
    };
  },
  computed: {
    // Built-in Hebrew faces plus whatever the organization uploaded in
    // Settings > Custom fonts. The store registers every uploaded face with the
    // browser as the library loads, so a picked one renders here immediately.
    fontFamilies() {
      return withCustomFontStackOptions(
        MINYANIM_TEXT_EDITOR_FONT_OPTIONS,
        storedCustomFonts(this.$store)
      );
    },
    editorStyle() {
      return {
        '--rte-height': this.height,
      };
    },
    // An action or Required tags need a wider menu than plain entries.
    variableMenuIsRich() {
      return Boolean(this.variableAction) || this.variables.some((variable) => variable.required);
    },
    // --- Tiptap toolbar reactive state ---
    currentFontFamily() {
      this.toolbarStateKey;
      return this.editor?.getAttributes('textStyle').fontFamily || '';
    },
    currentFontSize() {
      this.toolbarStateKey;
      const fontSize = this.editor?.getAttributes('textStyle').fontSize;
      return fontSize ? String(fontSize).replace('px', '') : '';
    },
    currentLineHeight() {
      this.toolbarStateKey;
      return (
        this.editor?.getAttributes('paragraph').lineHeight ||
        this.editor?.getAttributes('heading').lineHeight ||
        ''
      );
    },
    currentDirection() {
      this.toolbarStateKey;
      return (
        this.editor?.getAttributes('paragraph').textDirection ||
        this.editor?.getAttributes('heading').textDirection ||
        ''
      );
    },
    isRtlActive() {
      return this.currentDirection === 'rtl';
    },
    currentColor() {
      this.toolbarStateKey;
      return this.editor?.getAttributes('textStyle').color || '#111827';
    },
    currentHighlight() {
      this.toolbarStateKey;
      return this.editor?.getAttributes('highlight').color || '#4f46e5';
    },
    currentBlock() {
      this.toolbarStateKey;
      if (!this.editor) return 'paragraph';
      const level = this.editor.getAttributes('heading').level;
      return this.editor.isActive('heading') && level ? `h${level}` : 'paragraph';
    },
    isSplitActive() {
      this.toolbarStateKey;
      return Boolean(
        this.editor?.getAttributes('paragraph').splitRow ||
        this.editor?.getAttributes('heading').splitRow
      );
    },
    currentIndent() {
      this.toolbarStateKey;
      return Number(
        this.editor?.getAttributes('paragraph').indent ||
        this.editor?.getAttributes('heading').indent ||
        0
      );
    },
    // Inside a list the toolbar nests / lifts the item; everywhere else it moves
    // the block's own indent.
    isInList() {
      this.toolbarStateKey;
      return Boolean(this.editor?.isActive('listItem'));
    },
    // --- Photo slot, while one is selected ---
    // The size controls live up here rather than on the placeholder itself so a
    // box dragged down to a few percent of the width still has room to be sized
    // back up, and so nothing can ever be clipped by the edge of the canvas.
    isEventImageActive() {
      this.toolbarStateKey;
      return Boolean(this.editor?.isActive('eventImage'));
    },
    eventImageWidth() {
      this.toolbarStateKey;
      return clampEventImageWidth(this.editor?.getAttributes('eventImage').width);
    },
    eventImageSizeLabel() {
      this.toolbarStateKey;
      const attributes = this.editor?.getAttributes('eventImage') || {};
      const height = eventImageHeightPercent(attributes.width, attributes.ratio);
      return `${Math.round(clampEventImageWidth(attributes.width))}% × ${Math.round(height)}%`;
    },
  },
  watch: {
    modelValue(value) {
      if (!this.editor) return;
      // Avoid resetting the cursor/selection when the change originated here.
      const isSame = (value || '') === this.editor.getHTML();
      if (isSame) return;
      this.editor.commands.setContent(value || '', false);
      this.$emit('normalized', this.editor.getHTML());
    },
  },
  methods: {
    chain() {
      return this.editor.chain().focus();
    },
    // Alignment means two different things depending on the block. In a split
    // row the pieces are already flex columns, so aligning the paragraph would
    // do nothing visible - the alignment belongs to the selected piece instead.
    // Same rule the Minyanim Roll grid designer applies.
    isAlign(value) {
      this.toolbarStateKey;
      if (!this.editor) return false;
      if (this.isSplitActive) {
        return this.editor.getAttributes('textStyle').inlineAlign === value;
      }
      return this.editor.isActive({ textAlign: value });
    },
    setAlign(value) {
      if (!this.editor) return;
      if (this.isSplitActive) {
        // Select one variable to align just it; select the whole row to align
        // them together.
        this.chain().setMark('textStyle', { inlineAlign: value }).run();
        return;
      }
      this.chain().setTextAlign(value).run();
    },
    setBlock(value) {
      if (!this.editor) return;
      if (value === 'paragraph') {
        this.chain().setParagraph().run();
        return;
      }
      const level = Number(String(value).replace('h', ''));
      if (!Number.isFinite(level)) return;
      this.chain().setHeading({ level }).run();
    },
    toggleSplit() {
      if (!this.editor) return;
      if (this.isSplitActive) {
        this.chain().unsetSplitRow().run();
        return;
      }
      this.chain().setSplitRow().run();
    },
    indent(direction) {
      if (!this.editor) return;
      if (this.isInList) {
        // Nesting a list item is what indent means inside a list - a padding on
        // the item would leave its bullet behind.
        if (direction > 0) this.chain().sinkListItem('listItem').run();
        else this.chain().liftListItem('listItem').run();
        return;
      }
      this.chain().setIndent(this.currentIndent + direction * INDENT_STEP_EM).run();
    },
    toggleCharacterMenu() {
      this.showCharacterMenu = !this.showCharacterMenu;
      if (this.showCharacterMenu) {
        this.$nextTick(this.updateCharacterMenuPosition);
      }
    },
    updateCharacterMenuPosition() {
      if (!this.showCharacterMenu) return;
      this.positionMenu('characterButton', 'characterMenu', 'characterMenuStyle', 236);
    },
    insertCharacter(character) {
      this.showCharacterMenu = false;
      if (this.editor) {
        this.editor.chain().focus().insertContent(character).run();
      }
    },
    setFontFamily(value) {
      if (value) {
        this.chain().setFontFamily(value).run();
      } else {
        this.chain().unsetFontFamily().run();
      }
    },
    setFontSize(value) {
      if (value) {
        this.chain().setFontSize(String(value)).run();
      } else {
        this.chain().unsetFontSize().run();
      }
    },
    setLineHeight(value) {
      if (value) {
        this.chain().setLineHeight(String(value)).run();
      } else {
        this.chain().unsetLineHeight().run();
      }
    },
    toggleRtl() {
      const nextDirection = this.isRtlActive ? 'ltr' : 'rtl';
      const align = nextDirection === 'rtl' ? 'right' : 'left';
      this.chain().setTextDirection(nextDirection).setTextAlign(align).run();
    },
    setColor(value) {
      this.chain().setColor(value).run();
    },
    setHighlight(value) {
      this.chain().setHighlight({ color: value }).run();
    },
    toggleLink() {
      const previous = this.editor.getAttributes('link').href;
      const url = window.prompt('Enter URL', previous || 'https://');
      if (url === null) return;
      if (url === '') {
        this.chain().extendMarkRange('link').unsetLink().run();
        return;
      }
      this.chain().extendMarkRange('link').setLink({ href: url }).run();
    },
    addImage() {
      const url = window.prompt('Image URL');
      if (url) {
        this.chain().setImage({ src: url }).run();
      }
    },
    clearFormat() {
      this.chain().unsetAllMarks().clearNodes().run();
    },
    toggleVariableMenu() {
      this.showVariableMenu = !this.showVariableMenu;
      if (this.showVariableMenu) {
        // Hidden until placed, so it never shows for a frame where it last was.
        this.variableMenuStyle = { ...this.variableMenuStyle, visibility: 'hidden' };
        this.$nextTick(this.updateVariableMenuPosition);
      }
    },
    updateVariableMenuPosition() {
      if (!this.showVariableMenu) return;
      this.positionMenu(
        'variableButton',
        'variableMenu',
        'variableMenuStyle',
        this.$refs.variableMenu?.offsetWidth || 190
      );
    },
    // The menus are teleported to <body> so a toolbar that scrolls cannot clip
    // them; that means their position has to be computed against the viewport.
    // Flips above the button when there is no room below.
    positionMenu(buttonRef, menuRef, styleKey, menuWidth) {
      if (typeof window === 'undefined') return;
      const button = this.$refs[buttonRef];
      const menu = this.$refs[menuRef];
      if (!button) return;

      const viewportPadding = 8;
      const gap = 6;
      const rect = button.getBoundingClientRect();
      const menuHeight = menu?.offsetHeight || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const left = Math.min(
        Math.max(viewportPadding, rect.right - menuWidth),
        Math.max(viewportPadding, viewportWidth - menuWidth - viewportPadding)
      );
      let top = rect.bottom + gap;
      if (menuHeight && top + menuHeight > viewportHeight - viewportPadding) {
        top = Math.max(viewportPadding, rect.top - menuHeight - gap);
      }

      this[styleKey] = {
        top: `${top}px`,
        left: `${left}px`,
        visibility: 'visible',
      };
    },
    updateMenuPositions() {
      this.updateVariableMenuPosition();
      this.updateCharacterMenuPosition();
    },
    insertVariable(variable) {
      this.showVariableMenu = false;
      if (!this.editor) return;

      // A photo goes in as a box the author can then size, rather than as the
      // bare token: how much room the picture takes is the template's decision,
      // and there is nothing about an event's own photo to decide it from.
      const definition = this.variables.find((entry) => entry.value === variable);
      if (definition?.isImage) {
        this.editor.chain().focus().insertContent({ type: 'eventImage' }).run();
        return;
      }

      this.editor.chain().focus().insertContent(`{${variable}}`).run();
    },
    runVariableAction() {
      this.showVariableMenu = false;
      this.$emit('variable-action');
    },
    stepEventImageWidth(direction) {
      const width = clampEventImageWidth(
        this.eventImageWidth + direction * EVENT_IMAGE_WIDTH_STEP
      );
      if (width === this.eventImageWidth) return;
      this.updateEventImage({ width });
    },
    resetEventImageShape() {
      this.updateEventImage({ ratio: EVENT_IMAGE_DEFAULT_RATIO });
    },
    /**
     * Change the selected photo slot, and leave it selected.
     *
     * Changing a node's attributes replaces the node, and the selection sitting
     * on it does not survive that: ProseMirror maps the NodeSelection through
     * the step and hands back a collapsed cursor beside the node instead. Left
     * alone, the box would be deselected by the very press that resized it —
     * these controls would disappear after one click and the second press would
     * have nothing left to act on. So the selection is put back afterwards, at
     * a position an attribute change cannot have moved.
     */
    updateEventImage(attributes) {
      const position = this.eventImagePosition();
      if (position === null) return;

      this.editor
        .chain()
        .focus()
        .updateAttributes('eventImage', attributes)
        .setNodeSelection(position)
        .run();
    },
    // Where the selected slot sits, or null when the selection is not a slot.
    // Read off the selection rather than searched for, so a template holding
    // several photos always resizes the one the author actually clicked.
    eventImagePosition() {
      const selection = this.editor?.state?.selection;
      const node = selection?.node;
      if (!node || node.type.name !== 'eventImage') return null;
      return selection.from;
    },
    handleDocumentClick(event) {
      const target = event.target;
      if (
        this.showVariableMenu &&
        !target?.closest?.('.rte-variable') &&
        !target?.closest?.('.rte-variable__menu')
      ) {
        this.showVariableMenu = false;
      }
      if (
        this.showCharacterMenu &&
        !target?.closest?.('.rte-chars') &&
        !target?.closest?.('.rte-chars__menu')
      ) {
        this.showCharacterMenu = false;
      }
    },
    touchToolbarState() {
      this.toolbarStateKey += 1;
    },
  },
  mounted() {
    const starterKit = this.starterKitOptions
      ? StarterKit.configure(this.starterKitOptions)
      : StarterKit;
    this.editor = markRaw(
      new Editor({
        content: this.modelValue || DEFAULT_EDITOR_CONTENT,
        extensions: [
          starterKit,
          Underline,
          TextStyle,
          FontSize,
          Color,
          FontFamily,
          LineHeight,
          TextDirection,
          Highlight.configure({ multicolor: true }),
          Link.configure({ openOnClick: false, autolink: true }),
          Image,
          TextAlign.configure({ types: ['heading', 'paragraph'] }),
          // Always loaded, whether or not the toolbar offers their buttons, so
          // content authored elsewhere (the Minyanim Roll designer writes the
          // same split-row markup) round-trips through this editor untouched
          // instead of being stripped on parse.
          InlineAlign,
          SplitRow,
          Indent,
          EventImage,
          ...(Array.isArray(this.extensions) ? this.extensions : []),
        ],
        onCreate: () => {
          this.touchToolbarState();
          this.$emit('normalized', this.editor.getHTML());
        },
        onUpdate: () => {
          this.$emit('update:modelValue', this.editor.getHTML());
          this.touchToolbarState();
        },
        onSelectionUpdate: () => {
          this.touchToolbarState();
        },
        onFocus: () => {
          this.touchToolbarState();
        },
      })
    );
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.updateMenuPositions);
    window.addEventListener('scroll', this.updateMenuPositions, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.updateMenuPositions);
    window.removeEventListener('scroll', this.updateMenuPositions, true);
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
};
</script>

<style scoped>
.rte {
  border: 1px solid var(--color-border, #e1e5ec);
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
}

.rte--invalid {
  border-color: var(--color-danger, #dc2626);
}

.rte__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px;
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.rte__tb-divider {
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: #e5e7eb;
}

.rte__tb-spacer {
  flex: 1 1 auto;
}

.rte__tb-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 30px;
  height: 30px;
  padding: 0 7px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #374151;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.rte__tb-btn i {
  font-size: 13px;
}

.rte__tb-btn:hover {
  background: #f3f4f6;
}

.rte__tb-btn.is-active {
  border-color: #e0e4f6;
  background: #eef2ff;
  color: #4f46e5;
}

.rte__tb-btn--bold {
  font-weight: 700;
}

.rte__tb-btn--italic {
  font-style: italic;
}

.rte__tb-btn--underline {
  text-decoration: underline;
}

.rte__tb-btn--strike {
  text-decoration: line-through;
}

.rte__tb-btn--text-color {
  flex-direction: column;
  gap: 1px;
  font-weight: 600;
}

.rte__tb-color-bar {
  display: block;
  width: 16px;
  height: 3px;
  border-radius: 2px;
}

.rte__tb-swatch {
  display: block;
  width: 16px;
  height: 16px;
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 4px;
}

.rte__tb-color-input {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  margin: 0;
  padding: 0;
  border: none !important;
  opacity: 0;
  cursor: pointer;
}

.rte__tb-select {
  height: 30px !important;
  box-sizing: border-box;
  padding: 0 6px;
  border: 1px solid #e0e4eb !important;
  border-radius: 6px !important;
  background: #ffffff;
  color: #374151;
  font-size: 13px !important;
  line-height: 1 !important;
  cursor: pointer;
}

.rte__tb-select option {
  min-height: 24px;
  padding: 2px 6px;
  font-size: 13px;
  line-height: 1.15;
}

.rte__tb-select--small {
  width: 72px;
}

.rte__tb-select--zoom {
  width: 78px;
}

.rte-photo {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 4px;
  border: 1px solid #e0e4f6;
  border-radius: 8px;
  background: #eef2ff;
}
.rte-photo__label {
  padding: 0 4px;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 600;
}
.rte-photo__value {
  min-width: 76px;
  padding: 0 2px;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  font-variant-numeric: tabular-nums;
  /* "50% × 33%", never reordered by a right-to-left toolbar. */
  direction: ltr;
  unicode-bidi: isolate;
}
.rte-photo .rte__tb-btn {
  min-width: 24px;
  height: 24px;
  padding: 0 5px;
}
.rte-photo .rte__tb-btn i {
  font-size: 11px;
}

.rte-variable {
  position: relative;
  margin-left: auto;
}

.rte-variable__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 96px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #e0e4eb;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
}

.rte-variable__btn i {
  font-size: 10px;
}

.rte-variable__menu {
  position: fixed;
  z-index: 4000;
  width: 190px;
  max-height: calc(100dvh - 16px);
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
}

.rte-variable__menu button {
  display: block;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #1f2937;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.rte-variable__menu button:hover {
  background: #f5f6f8;
}

.rte-variable__menu--rich {
  width: 252px;
}

.rte-variable__menu .rte-variable__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rte-variable__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rte-variable__required {
  flex-shrink: 0;
  color: var(--color-danger, #ef4444);
  font-size: 11.5px;
  font-weight: 500;
}

.rte-variable__divider {
  height: 1px;
  margin: 6px 4px;
  background: #eef0f4;
}

.rte-variable__menu .rte-variable__action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4f46e5;
  font-weight: 500;
}

.rte-variable__menu .rte-variable__action:hover {
  background: #eef0ff;
}

.rte-variable__action i {
  font-size: 11px;
}

.rte-chars {
  position: relative;
  display: inline-flex;
}

.rte-chars__menu {
  position: fixed;
  z-index: 4000;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 2px;
  width: 236px;
  max-height: 240px;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid var(--color-border, #e1e5ec);
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
}

.rte-chars__menu button {
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
}

.rte-chars__menu button:hover {
  background: #eef2ff;
}

/* A split row is laid out by its own inline style; this only keeps its pieces
   from collapsing to zero width while the row is being authored. */
.rte__editor :deep([data-split="true"]) > * {
  min-width: 0;
}

.rte__canvas {
  padding: 0;
}

.rte__editor {
  background: #ffffff;
}

.rte__editor :deep(.ProseMirror) {
  min-height: var(--rte-height, 320px);
  padding: 12px 14px;
  outline: none;
  color: #111827;
  font-size: 14px;
  line-height: 1.5;
}

.rte__editor :deep(.ProseMirror > *:first-child) {
  margin-top: 0;
}

.rte__editor :deep(.ProseMirror p) {
  margin: 0 0 8px;
}

.rte__editor :deep(.ProseMirror ul),
.rte__editor :deep(.ProseMirror ol) {
  margin: 0 0 8px;
  padding-left: 22px;
}

.rte__editor :deep(.ProseMirror a) {
  color: #4f46e5;
  text-decoration: underline;
}

.rte__editor :deep([dir="rtl"]) {
  direction: rtl;
  text-align: right;
}

.rte__editor :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}
</style>
